import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import client from "./graphql/client";
import { AuthUserProvider } from "./contexts/AuthUserContext";
import { useAuthUser } from "./contexts/AuthUserContext";
import { LocalizationProvider } from "./contexts/LocalizationContext";
import { ShopProvider } from "./contexts/ShopContext";
import { SupportCenterProvider } from "./contexts/SupportCenterContext";
import { PageHeaderProvider } from "./contexts/PageHeaderContext";
import NotificationSocketBridge from "./components/NotificationSocketBridge";
import AppShell from "./components/AppShell";
import HomePage from "./pages/HomePage/index.jsx";
import LoginPage from "./pages/LoginPage/index.jsx";
import NotificationsPage from "./pages/NotificationsPage/index.jsx";
import ReportsPage from "./pages/ReportsPage/index.jsx";
import ReportDetailPage from "./pages/ReportDetailPage/index.jsx";
import ReportPrintPage from "./pages/ReportPrintPage/index.jsx";
import MetaReportOpenPage from "./pages/MetaReportOpenPage/index.jsx";
import GenericReportOpenPage from "./pages/GenericReportOpenPage/index.jsx";
import SettingsPage from "./pages/SettingsPage/index.jsx";
import ChatPage from "./pages/ChatPage/index.jsx";
import AuthCallbackPage from "./pages/AuthCallbackPage/index.jsx";
import HelpCenterPage from "./pages/HelpCenterPage/index.jsx";
import ProductCostsPage from "./pages/ProductCostsPage/index.jsx";
import OperatingCostsPage from "./pages/OperatingCostsPage/index.jsx";
import StrategyReportPage from "./pages/StrategyReportPage/index.jsx";
import BillingPage from "./pages/BillingPage/index.jsx";
import IntegrationsPage from "./pages/IntegrationsPage/index.jsx";
import BrandPage from "./pages/BrandPage/index.jsx";
import BrandPrintPage from "./pages/BrandPrintPage/index.jsx";
import EmailSignupPage from "./pages/EmailSignupPage/index.jsx";
import EmailVerifyPage from "./pages/EmailVerifyPage/index.jsx";
import BusinessDomainPage from "./pages/BusinessDomainPage/index.jsx";

const withShell = (element) => <AppShell>{element}</AppShell>;

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuthUser();

  if (loading) return null;
  if (!user) return <Navigate replace to="/login" />;

  return children;
};

const OnboardingRoute = ({ children }) => {
  const { user, loading } = useAuthUser();

  if (loading) return null;
  if (!user) return <Navigate replace to="/login" />;
  return children;
};

function App() {
  return (
    <ApolloProvider client={client}>
      <AuthUserProvider>
        <ShopProvider>
          <LocalizationProvider>
            <SupportCenterProvider>
              <PageHeaderProvider>
              <NotificationSocketBridge />
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Routes>
                  {/* Public auth routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/login/email" element={<EmailSignupPage />} />
                  <Route path="/login/email/verify" element={<EmailVerifyPage />} />
                  <Route path="/auth/callback" element={<AuthCallbackPage />} />

                  {/* Onboarding routes - require auth but not full onboarding */}
                  <Route
                    path="/onboarding/domain"
                    element={<OnboardingRoute><BusinessDomainPage /></OnboardingRoute>}
                  />
                  <Route
                    path="/onboarding/integrations"
                    element={<OnboardingRoute>{withShell(<IntegrationsPage />)}</OnboardingRoute>}
                  />

                  {/* Protected app routes */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>{withShell(<HomePage />)}</ProtectedRoute>
                    }
                  />
                  <Route
                    path="/assessment"
                    element={
                      <ProtectedRoute>
                        {withShell(<HomePage forcedTabKey="assessment" />)}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={
                      <ProtectedRoute>
                        {withShell(<NotificationsPage />)}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute>{withShell(<ReportsPage />)}</ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/:reportId"
                    element={
                      <ProtectedRoute>{withShell(<ReportDetailPage />)}</ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/open/meta"
                    element={
                      <ProtectedRoute>{withShell(<MetaReportOpenPage />)}</ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/open/:key"
                    element={
                      <ProtectedRoute>{withShell(<GenericReportOpenPage />)}</ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/:reportId/print"
                    element={
                      <ProtectedRoute><ReportPrintPage /></ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>{withShell(<SettingsPage />)}</ProtectedRoute>
                    }
                  />
                  <Route
                    path="/chat"
                    element={
                      <ProtectedRoute>
                        <ChatPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/help-center"
                    element={
                      <ProtectedRoute>
                        {withShell(<HelpCenterPage />)}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/product-costs"
                    element={
                      <ProtectedRoute>
                        {withShell(<ProductCostsPage />)}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/operating-costs"
                    element={
                      <ProtectedRoute>
                        {withShell(<OperatingCostsPage />)}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/strategy-report"
                    element={
                      <ProtectedRoute>
                        {withShell(<StrategyReportPage />)}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/key-focus"
                    element={
                      <ProtectedRoute>
                        {withShell(<HomePage forcedTabKey="keyFocus" />)}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/billing"
                    element={
                      <ProtectedRoute>
                        {withShell(<BillingPage />)}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/integrations"
                    element={
                      <ProtectedRoute>
                        {withShell(<IntegrationsPage />)}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/brand"
                    element={
                      <ProtectedRoute>
                        {withShell(<BrandPage />)}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/brand/print"
                    element={
                      <ProtectedRoute><BrandPrintPage /></ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<Navigate replace to="/" />} />
                </Routes>
              </BrowserRouter>
              </PageHeaderProvider>
            </SupportCenterProvider>
          </LocalizationProvider>
        </ShopProvider>
      </AuthUserProvider>
    </ApolloProvider>
  );
}

export default App;
